import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getWorkspaceAvailability } from "../../api/workspaces";
import { getPublicWorkspace } from "../../api/workspaceMarketplace";
import { useAuth } from "../../context/AuthContext";
import MarketplaceShell from "./MarketplaceShell";
import {
  formatWorkspaceDateTime,
  formatWorkspaceMoney,
  getWorkspaceCheckoutPath,
  getWorkspaceDisplayName,
  getWorkspaceLocation,
  titleCaseLabel,
} from "../../utils/workspacePresentation";
import {
  getWorkspaceFinanceSnapshot,
  getWorkspacePaymentState,
  paymentToneClasses,
} from "../../utils/workspaceBillingPresentation";
import { resolveAssetUrl } from "../../utils/assetUrl";
import { getMarketplaceOperatorWorkspacesPath } from "../../utils/marketplaceOperatorRoutes";

function toIsoDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function defaultTimeWindow() {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  start.setHours(start.getHours() + 1);
  const end = new Date(start);
  end.setHours(end.getHours() + 4);
  return {
    start: start.toISOString().slice(0, 16),
    end: end.toISOString().slice(0, 16),
  };
}

function DetailListItem({ label, value }) {
  return (
    <div>
      <dt className="font-semibold text-slate-900">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export default function MarketplaceWorkspaceDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { token, loading: authLoading } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [availabilityWindow, setAvailabilityWindow] = useState(defaultTimeWindow);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const isPreviewMode = searchParams.get("preview") === "1";
  const workspaceListingsPath = `${getMarketplaceOperatorWorkspacesPath()}?tab=listings`;
  const returnTo = useMemo(() => {
    const requested = String(searchParams.get("returnTo") || "").trim();
    if (!requested || !requested.startsWith("/") || requested.startsWith("//")) {
      return "/account/operator/workspaces";
    }
    return requested;
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    if (isPreviewMode && authLoading) {
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setError("");
    if (isPreviewMode && !token) {
      setWorkspace(null);
      setError("Sign in to preview this workspace.");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    getPublicWorkspace(id, isPreviewMode ? token : null)
      .then((data) => {
        if (active) {
          setWorkspace(data);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load workspace.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [authLoading, id, isPreviewMode, token]);

  const selectedAmenities = useMemo(
    () => (Array.isArray(workspace?.amenity_addons) ? workspace.amenity_addons : []),
    [workspace?.amenity_addons]
  );

  const gallery = useMemo(() => {
    if (Array.isArray(workspace?.photos) && workspace.photos.length) {
      return workspace.photos;
    }
    return [];
  }, [workspace?.photos]);
  const visibleGallery = useMemo(() => gallery.slice(0, 4), [gallery]);
  const hiddenPhotoCount = Math.max(0, gallery.length - visibleGallery.length);
  const activePhoto = activePhotoIndex === null ? null : gallery[activePhotoIndex];

  useEffect(() => {
    if (activePhotoIndex === null) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePhotoIndex(null);
      }
      if (event.key === "ArrowLeft") {
        setActivePhotoIndex((current) =>
          current === null ? current : Math.max(0, current - 1)
        );
      }
      if (event.key === "ArrowRight") {
        setActivePhotoIndex((current) =>
          current === null ? current : Math.min(gallery.length - 1, current + 1)
        );
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePhotoIndex, gallery.length]);

  const pricingSnapshot = useMemo(() => getWorkspaceFinanceSnapshot(workspace || {}), [workspace]);

  const draftPaymentState = useMemo(
    () =>
      getWorkspacePaymentState({
        status: workspace?.requires_approval ? "pending" : "confirmed",
        payment_status: workspace?.requires_approval
          ? "approval_pending"
          : workspace?.deposit_amount
          ? "deposit_due"
          : "payment_pending",
      }),
    [workspace?.deposit_amount, workspace?.requires_approval]
  );

  const handleAvailabilityCheck = async (event) => {
    event.preventDefault();
    const start_time = toIsoDateTime(availabilityWindow.start);
    const end_time = toIsoDateTime(availabilityWindow.end);
    if (!start_time || !end_time) {
      setAvailability({
        available: false,
        reason: "Choose a valid start and end time.",
      });
      return;
    }
    setAvailabilityLoading(true);
    try {
      const result = await getWorkspaceAvailability(workspace.id, {
        start_time,
        end_time,
      });
      setAvailability(result);
    } catch (err) {
      setAvailability({
        available: false,
        reason:
          err?.payload?.detail?.message ||
          err.message ||
          "Unable to check availability.",
      });
    } finally {
      setAvailabilityLoading(false);
    }
  };

  return (
    <MarketplaceShell>
      {isPreviewMode ? (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-900 shadow-sm">
          <div>
            <p className="font-semibold">Workspace listing preview</p>
            <p className="mt-1 text-sky-800">
              This preview shows the saved listing details before renters see it publicly.
            </p>
          </div>
          <Link
            to={returnTo}
            className="inline-flex items-center justify-center rounded-full border border-sky-300 bg-white px-5 py-2.5 text-sm font-semibold text-sky-800 hover:border-sky-400"
          >
            Back to Workspace Setup
          </Link>
        </div>
      ) : null}
      {!isPreviewMode && token ? (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-700 shadow-sm">
          <div>
            <p className="font-semibold text-slate-900">Managing this workspace?</p>
            <p className="mt-1 text-slate-600">
              Return to your workspace list to continue setup, agreements, or edits.
            </p>
          </div>
          <Link
            to={workspaceListingsPath}
            className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Back to Workspace Listings
          </Link>
        </div>
      ) : null}
      <section className="grid gap-6 xl:grid-cols-[1.12fr,0.88fr]">
        <div className="space-y-6">
          <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <div className="grid gap-0 lg:grid-cols-[1.08fr,0.92fr]">
              <div className="min-h-[340px] bg-gradient-to-br from-[#2f3566] via-slate-800 to-slate-900 p-6 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-200">
                  Ellyanna Boothshare
                </p>
                {loading ? (
                  <p className="mt-8 text-sm text-slate-200">Loading workspace...</p>
                ) : error ? (
                  <div className="mt-8 rounded-2xl border border-rose-300/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                    {error}
                  </div>
                ) : (
                  <>
                    <div className="mt-6 inline-flex rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-100">
                      {titleCaseLabel(workspace?.workspace_type, "Workspace")}
                    </div>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight">
                      {getWorkspaceDisplayName(workspace)}
                    </h1>
                    <p className="mt-3 text-sm text-slate-200 md:text-base">
                      {workspace?.salon?.name || "Ellyanna salon"}{" "}
                      <span className="px-1 text-slate-300">/</span>{" "}
                      {getWorkspaceLocation(workspace)}
                    </p>
                    <p className="mt-5 max-w-2xl text-sm text-slate-200 md:text-base">
                      {workspace?.description ||
                        "Premium salon workspace managed through the same Ellyanna operating system used for schedules, reporting, and business setup."}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold">
                        {formatWorkspaceMoney(workspace?.price_per_hour)}/hr
                      </span>
                      {workspace?.price_per_day ? (
                        <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold">
                          {formatWorkspaceMoney(workspace.price_per_day)}/day
                        </span>
                      ) : null}
                      {workspace?.price_per_week ? (
                        <span className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold">
                          {formatWorkspaceMoney(workspace.price_per_week)}/week
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
              <div className="grid gap-3 bg-slate-50 p-6 sm:grid-cols-2">
                {visibleGallery.length ? (
                  visibleGallery.map((photo, index) => (
                    <button
                      key={`${photo}-${index}`}
                      type="button"
                      aria-label={`Open photo ${index + 1} of ${gallery.length}`}
                      onClick={() => setActivePhotoIndex(index)}
                      className="relative min-h-[120px] overflow-hidden rounded-2xl border border-slate-200 bg-cover bg-center text-left transition hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                      style={{ backgroundImage: `url("${resolveAssetUrl(photo)}")` }}
                    >
                      {index === visibleGallery.length - 1 && hiddenPhotoCount ? (
                        <span className="absolute inset-0 grid place-items-center bg-slate-950/62 px-3 text-center text-lg font-semibold text-white">
                          +{hiddenPhotoCount} photo{hiddenPhotoCount === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </button>
                  ))
                ) : (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <p className="text-sm font-semibold text-slate-900">
                        Same product family
                      </p>
                      <p className="mt-2 text-sm text-slate-600">
                        This listing inherits Ellyanna controls for approvals,
                        scheduling, reporting, and business-level defaults.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                      <p className="text-sm font-semibold text-slate-900">
                        Marketplace-ready operations
                      </p>
                      <p className="mt-2 text-sm text-slate-600">
                        Public discovery stays connected to the same admin
                        system used by the salon to run daily operations.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </article>

          {!loading && !error && workspace ? (
            <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                    Listing Details
                  </p>
                  <dl className="mt-4 grid gap-4 text-sm text-slate-600">
                    <DetailListItem
                      label="Workspace type"
                      value={titleCaseLabel(workspace.workspace_type, "Workspace")}
                    />
                    <DetailListItem
                      label="Rental request mode"
                      value={titleCaseLabel(workspace.booking_mode, "Internal")}
                    />
                    <DetailListItem
                      label="Availability window"
                      value={
                        [workspace.available_start_time, workspace.available_end_time]
                          .filter(Boolean)
                          .join(" - ") || "Salon hours"
                      }
                    />
                    <DetailListItem
                      label="Deposit"
                      value={
                        workspace.deposit_amount
                          ? formatWorkspaceMoney(workspace.deposit_amount)
                          : "Not required"
                      }
                    />
                    <DetailListItem
                      label="Cleaning fee"
                      value={
                        workspace.cleaning_fee
                          ? formatWorkspaceMoney(workspace.cleaning_fee)
                          : "Included"
                      }
                    />
                    <DetailListItem
                      label="Payment timing"
                      value={draftPaymentState.message}
                    />
                  </dl>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                    Policies
                  </p>
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    <p className="font-semibold text-slate-900">
                      {workspace.policy_set?.name || "Workspace policy"}
                    </p>
                    <p className="mt-3 whitespace-pre-line">
                      {workspace.policy_set?.house_rules_text ||
                        "House rules will be confirmed by the salon during rental request review."}
                    </p>
                    {workspace.policy_set?.cancellation_policy_text ? (
                      <p className="mt-4 whitespace-pre-line border-t border-slate-200 pt-4">
                        {workspace.policy_set.cancellation_policy_text}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-6 md:grid-cols-[1fr,1fr]">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                    Amenities
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {selectedAmenities.length ? (
                      selectedAmenities.map((addon, index) => (
                        <span
                          key={`${addon?.key || addon?.label || "amenity"}-${index}`}
                          className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
                        >
                          {addon?.label || addon?.name || addon?.key || "Amenity"}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                        Shared salon amenities
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                    Salon details
                  </p>
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    <p className="font-semibold text-slate-900">
                      {workspace.salon?.name || "Ellyanna salon"}
                    </p>
                    <p className="mt-2">{getWorkspaceLocation(workspace)}</p>
                    {workspace.salon?.address ? (
                      <p className="mt-2">{workspace.salon.address}</p>
                    ) : null}
                  </div>
                </div>
              </div>
            </article>
          ) : null}
        </div>

        <aside className="space-y-6">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                  Check Availability
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  Confirm an open time range before moving into checkout.
                </p>
              </div>
              <span
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${paymentToneClasses(
                  draftPaymentState.tone
                )}`}
              >
                {draftPaymentState.label}
              </span>
            </div>
            <form className="mt-4 space-y-4" onSubmit={handleAvailabilityCheck}>
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-900">Start</span>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={availabilityWindow.start}
                  onChange={(event) =>
                    setAvailabilityWindow((prev) => ({
                      ...prev,
                      start: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="block space-y-2">
                <span className="text-sm font-semibold text-slate-900">End</span>
                <input
                  type="datetime-local"
                  className="h-11 w-full rounded-xl border border-slate-300 px-4 text-sm text-slate-900"
                  value={availabilityWindow.end}
                  onChange={(event) =>
                    setAvailabilityWindow((prev) => ({
                      ...prev,
                      end: event.target.value,
                    }))
                  }
                />
              </label>
              <button
                type="submit"
                disabled={availabilityLoading || !workspace?.id}
                className="w-full rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {availabilityLoading ? "Checking..." : "Check Availability"}
              </button>
            </form>
            {availability ? (
              <div
                className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                  availability.available
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-rose-200 bg-rose-50 text-rose-700"
                }`}
              >
                {availability.available
                  ? "This workspace is open for the selected time range."
                  : availability.reason || "This workspace is not available."}
                {!availability.available && Array.isArray(availability.blocked_ranges)
                  ? availability.blocked_ranges.slice(0, 2).map((range, index) => (
                      <div key={`blocked-${index}`} className="mt-2 text-xs">
                        {formatWorkspaceDateTime(range?.starts_at)} to{" "}
                        {formatWorkspaceDateTime(range?.ends_at)}
                      </div>
                    ))
                  : null}
              </div>
            ) : null}
            <Link
              to={workspace ? getWorkspaceCheckoutPath(workspace) : `/checkout/${id}`}
              className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-rose-500 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-600"
            >
              Continue To Checkout
            </Link>
          </article>

          {!loading && workspace ? (
            <article className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                Pricing Snapshot
              </p>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <div className="flex items-center justify-between">
                  <span>Base hourly rate</span>
                  <span className="font-semibold text-slate-900">
                    {formatWorkspaceMoney(workspace.price_per_hour)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Estimated add-ons</span>
                  <span className="font-semibold text-slate-900">
                    {formatWorkspaceMoney(pricingSnapshot.addonsTotalAmount)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Cleaning fee</span>
                  <span className="font-semibold text-slate-900">
                    {workspace.cleaning_fee
                      ? formatWorkspaceMoney(workspace.cleaning_fee)
                      : "Included"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Deposit</span>
                  <span className="font-semibold text-slate-900">
                    {workspace.deposit_amount
                      ? formatWorkspaceMoney(workspace.deposit_amount)
                      : "Not required"}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 pt-3">
                  <span>Due now</span>
                  <span className="font-semibold text-slate-900">
                    {formatWorkspaceMoney(pricingSnapshot.amountDueNow)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Due later</span>
                  <span className="font-semibold text-slate-900">
                    {formatWorkspaceMoney(pricingSnapshot.amountDueLater)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Estimated total</span>
                  <span className="font-semibold text-slate-900">
                    {formatWorkspaceMoney(pricingSnapshot.estimatedTotal)}
                  </span>
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Payment timing
                  </div>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {draftPaymentState.message}
                  </p>
                </div>
              </div>
            </article>
          ) : null}
        </aside>
      </section>
      {activePhoto ? (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-slate-950/80 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-label="Workspace photo gallery"
        >
          <div className="relative w-full max-w-5xl rounded-2xl bg-white p-3 shadow-2xl">
            <div className="flex items-center justify-between gap-3 px-2 pb-3">
              <div className="text-sm font-semibold text-slate-700">
                Photo {activePhotoIndex + 1} of {gallery.length}
              </div>
              <button
                type="button"
                onClick={() => setActivePhotoIndex(null)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <div className="relative overflow-hidden rounded-xl bg-slate-100">
              <img
                src={resolveAssetUrl(activePhoto)}
                alt={`Workspace ${activePhotoIndex + 1} of ${gallery.length}`}
                className="max-h-[72vh] w-full object-contain"
              />
              <button
                type="button"
                onClick={() => setActivePhotoIndex((current) => Math.max(0, current - 1))}
                disabled={activePhotoIndex === 0}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-4 py-3 text-sm font-semibold text-slate-900 shadow disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setActivePhotoIndex((current) => Math.min(gallery.length - 1, current + 1))
                }
                disabled={activePhotoIndex >= gallery.length - 1}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 px-4 py-3 text-sm font-semibold text-slate-900 shadow disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </MarketplaceShell>
  );
}
